import React from 'react';
/**
 * `SubscriptionPaymentDisplay` renders subscription (recurring) pricing text.
 *
 * Behaviour:
 * - Formats the price using the provided `culture` and current currency symbol.
 * - Uses i18n strings for "from" and "/month" text.
 * - When `isForMultipleDuration` is true, prefixes the output with a payment
 *   method label to distinguish multiple pricing options.
 */
import { formatNumber } from '../../../common/Formatter';
import { useCurrencySymbol } from '../../../../hooks/useCurrencySymbol';
import { useTranslation } from 'react-i18next';

type SubscriptionPaymentDisplayProps = {
  price: number;
  culture: string;
  isForMultipleDuration?: boolean;
};

const SubscriptionPaymentDisplay = ({
  price,
  culture,
  isForMultipleDuration = false
}: SubscriptionPaymentDisplayProps): JSX.Element => {
  const { t } = useTranslation();
  const currencySymbol = useCurrencySymbol();
  const subscriptionPrefixFromText = t(
    'renewals-quote.subscription-prefix-from-text'
  );
  const subscriptionPerMonthText = t(
    'renewals-quote.subscription-per-month-text'
  );
  const paymentMethodText = t(
    'renewals-quote.subscription-payment-method-text'
  );
  return (
    <p>
      {isForMultipleDuration && <span>{paymentMethodText} </span>}
      <span>{subscriptionPrefixFromText}</span>
      <span className={'p1-bold'}>
        {currencySymbol}
        {formatNumber(price, 2, culture)}
      </span>
      <span>{subscriptionPerMonthText}</span>
    </p>
  );
};

export default SubscriptionPaymentDisplay;
